import { useRef, useEffect, useCallback, useMemo } from "react";
import { currency } from "../../lib/formatting";
import { computeBucketAllocations } from "../../lib/calculations";
import { getEntityColor } from "../../lib/entityColors";
import { InfoTooltip } from "../ui/InfoTooltip";
import type { BudgetSettings } from "../../types/budget";

interface BucketFlowProps {
  budgetSettings: BudgetSettings;
  monthlySurplus: number;
}

export default function BucketFlow({ budgetSettings, monthlySurplus }: BucketFlowProps) {
  const gridRef = useRef<HTMLDivElement>(null);
  const svgRef = useRef<SVGSVGElement>(null);

  const allAllocations = useMemo(
    () => computeBucketAllocations(budgetSettings.buckets, monthlySurplus),
    [budgetSettings.buckets, monthlySurplus]
  );

  const destinations = useMemo(
    () => allAllocations.filter((a) => a.bucket.type !== "property"),
    [allAllocations]
  );

  const totalActual = destinations.reduce((s, a) => s + a.actual, 0);
  const remaining = Math.max(0, monthlySurplus - allAllocations.reduce((s, a) => s + a.actual, 0));

  // Property-prioritised flag: property bucket is above all non-property buckets
  const propAlloc = allAllocations.find((a) => a.bucket.type === "property");
  const propertyIndex = budgetSettings.buckets.findIndex((b) => b.type === "property");
  const firstNonPropertyIndex = budgetSettings.buckets.findIndex((b) => b.type !== "property");
  const propertyPrioritised = propertyIndex !== -1 && firstNonPropertyIndex !== -1 && propertyIndex < firstNonPropertyIndex;

  const sourceLabel = propertyPrioritised ? "Surplus after property" : "Monthly Surplus";
  const sourceValue = propertyPrioritised ? monthlySurplus - (propAlloc?.actual ?? 0) : monthlySurplus;
  const sourceSublabel = propertyPrioritised ? `Income − expenses − ${currency(Math.round(propAlloc?.actual ?? 0))} property` : "Income − expenses";

  const drawLines = useCallback(() => {
    const grid = gridRef.current;
    const svg = svgRef.current;
    if (!grid || !svg || window.innerWidth < 768) { if (svg) Array.from(svg.querySelectorAll("path:not(defs path)")).forEach(p => p.remove()); return; }
    const gR = grid.getBoundingClientRect();
    svg.setAttribute("viewBox", `0 0 ${gR.width} ${gR.height}`);
    svg.style.width = `${gR.width}px`;
    svg.style.height = `${gR.height}px`;
    let paths = "";
    const pool = grid.querySelector("[data-pool]");
    if (!pool) { Array.from(svg.querySelectorAll("path:not(defs path)")).forEach(p => p.remove()); return; }
    const pR = pool.getBoundingClientRect();
    const poolLeftX = pR.left - gR.left;
    const poolRightX = pR.right - gR.left;
    const poolTop = pR.top - gR.top;
    const poolH = pR.height;
    const sourceEls = Array.from(grid.querySelectorAll("[data-source]"));
    const destEls = Array.from(grid.querySelectorAll("[data-dest]"));
    const sourceMaxR = sourceEls.length > 0 ? Math.max(...sourceEls.map((el) => el.getBoundingClientRect().right - gR.left)) : poolLeftX;
    const destMinL = destEls.length > 0 ? Math.min(...destEls.map((el) => el.getBoundingClientRect().left - gR.left)) : poolRightX;
    const pullS = Math.max(10, Math.min(40, (poolLeftX - sourceMaxR) * 0.4));
    const pullD = Math.max(10, Math.min(40, (destMinL - poolRightX) * 0.4));

    sourceEls.forEach((el, i) => {
      const r = el.getBoundingClientRect();
      const x = r.right - gR.left, y = r.top + r.height / 2 - gR.top;
      const aY = poolTop + (poolH * (i + 1)) / (sourceEls.length + 1);
      paths += `<path marker-end="url(#arrow-bucket)" d="M ${x} ${y} C ${x + pullS} ${y}, ${poolLeftX - pullS} ${aY}, ${poolLeftX} ${aY}" />`;
    });
    destEls.forEach((el, i) => {
      const r = el.getBoundingClientRect();
      const x = r.left - gR.left, y = r.top + r.height / 2 - gR.top;
      const aY = poolTop + (poolH * (i + 1)) / (destEls.length + 1);
      paths += `<path marker-end="url(#arrow-bucket)" d="M ${poolRightX} ${aY} C ${poolRightX + pullD} ${aY}, ${x - pullD} ${y}, ${x} ${y}" />`;
    });
    Array.from(svg.querySelectorAll("path:not(defs path)")).forEach(p => p.remove());
    if (paths) {
      const tmp = new DOMParser().parseFromString(`<svg xmlns="http://www.w3.org/2000/svg">${paths}</svg>`, "image/svg+xml");
      Array.from(tmp.documentElement.children).forEach(child => svg.appendChild(child));
    }
  }, []);

  useEffect(() => {
    const t = setTimeout(drawLines, 80);
    const h = () => setTimeout(drawLines, 80);
    window.addEventListener("resize", h);
    return () => { clearTimeout(t); window.removeEventListener("resize", h); };
  }, [drawLines, destinations.length]);

  if (destinations.length === 0) {
    return <div className="rounded-xl border-2 border-dashed border-neutral-200 px-6 py-6 text-center text-sm text-neutral-500">No non-property buckets configured yet. Set up buckets on the Personal Budget page.</div>;
  }

  return (
    <div className="space-y-4">
      <div className="relative" ref={gridRef}>
        <svg ref={svgRef} className="absolute inset-0 pointer-events-none hidden md:block" style={{ overflow: "visible" }}>
          <defs>
            <marker id="arrow-bucket" viewBox="0 0 10 10" refX="9" refY="5" markerWidth="6" markerHeight="6" orient="auto-start-reverse" markerUnits="userSpaceOnUse">
              <path d="M 0 0 L 10 5 L 0 10 z" fill="#d6d3d1" />
            </marker>
          </defs>
          <style>{`svg path { fill: none; stroke: #d6d3d1; stroke-width: 1.5; }`}</style>
        </svg>

        {/* Desktop */}
        <div className="hidden md:grid md:grid-cols-[1fr_180px_1fr] gap-x-12 gap-y-4 items-start">
          <div className="space-y-3">
            <div className="text-[10px] font-medium uppercase tracking-wide text-neutral-400">Source</div>
            <div data-source className="rounded-xl border border-neutral-200 bg-white px-4 py-3">
              <div className="flex items-center gap-1 text-xs font-medium text-neutral-600">
                {sourceLabel}
                {propertyPrioritised && <InfoTooltip tip="Your monthly surplus, minus any allocation that goes to your property portfolio first. Reorder buckets on the Personal Budget page to change priority." />}
              </div>
              <div className="text-[10px] text-neutral-400">{sourceSublabel}</div>
              <div className="mt-1 text-sm font-semibold text-neutral-900">{currency(Math.round(sourceValue))}<span className="text-xs font-normal text-neutral-400">/mo</span></div>
            </div>
          </div>

          <div className="flex items-center justify-center" style={{ minHeight: 160 }}>
            <div data-pool className="rounded-2xl border border-neutral-200 bg-white px-5 py-5 text-center w-full">
              <div className="text-[10px] font-medium uppercase tracking-wide text-neutral-400">Funded</div>
              <div className="mt-1 text-lg font-bold text-neutral-900">{currency(Math.round(totalActual))}<span className="text-xs font-normal text-neutral-400">/mo</span></div>
            </div>
          </div>

          <div className="space-y-2">
            <div className="text-[10px] font-medium uppercase tracking-wide text-neutral-400">Buckets</div>
            {destinations.map((a, i) => {
              const b = a.bucket;
              const color = getEntityColor(i);
              const acct = [b.accountBankName, b.accountNumber ? `****${b.accountNumber.slice(-4)}` : null].filter(Boolean).join(" · ");
              return (
                <div key={b.id} data-dest className={`rounded-xl border bg-white px-4 py-3 ${a.actual === 0 ? "opacity-50" : ""}`} style={{ borderRightWidth: 4, borderRightColor: color, borderColor: undefined }}>
                  <div className="flex items-center gap-2">
                    <span className="inline-block h-2 w-2 rounded-full shrink-0" style={{ background: color }} />
                    <span className="text-sm font-medium text-neutral-900 truncate">{b.name}</span>
                  </div>
                  {b.description && <div className="text-[10px] text-neutral-400 truncate mt-0.5">{b.description}</div>}
                  <div className="mt-1 flex items-center justify-between">
                    <span className="text-sm font-semibold text-neutral-800">{currency(Math.round(a.actual))}/mo</span>
                    {acct && <span className="text-[10px] text-neutral-400">{acct}</span>}
                  </div>
                  {!a.fullyFunded && (
                    <div className="text-[10px] text-neutral-400 mt-0.5">
                      {a.actual === 0 ? `Unfunded — ${currency(Math.round(a.requested))}/mo requested` : `of ${currency(Math.round(a.requested))} requested`}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Mobile */}
        <div className="md:hidden space-y-4">
          <div>
            <div className="text-[10px] font-medium uppercase tracking-wide text-neutral-400 mb-2">Source</div>
            <div className="rounded-xl border border-neutral-200 bg-white px-4 py-3 flex items-center justify-between">
              <div>
                <div className="flex items-center gap-1 text-xs font-medium text-neutral-600">
                  {sourceLabel}
                  {propertyPrioritised && <InfoTooltip tip="Your monthly surplus, minus any allocation that goes to your property portfolio first. Reorder buckets on the Personal Budget page to change priority." />}
                </div>
                <div className="text-[10px] text-neutral-400">{sourceSublabel}</div>
              </div>
              <div className="text-sm font-semibold text-neutral-900">{currency(Math.round(sourceValue))}/mo</div>
            </div>
          </div>
          <div className="flex justify-center">
            <div className="rounded-2xl border border-neutral-200 bg-white px-6 py-3 text-center">
              <div className="text-[10px] uppercase tracking-wide text-neutral-400">Funded</div>
              <div className="text-lg font-bold text-neutral-900">{currency(Math.round(totalActual))}/mo</div>
            </div>
          </div>
          <div>
            <div className="text-[10px] font-medium uppercase tracking-wide text-neutral-400 mb-2">Buckets</div>
            <div className="space-y-2">
              {destinations.map((a, i) => {
                const b = a.bucket;
                const color = getEntityColor(i);
                return (
                  <div key={b.id} className={`rounded-xl border border-neutral-200 bg-white px-4 py-3 ${a.actual === 0 ? "opacity-50" : ""}`} style={{ borderLeftWidth: 4, borderLeftColor: color }}>
                    <div className="text-sm font-medium text-neutral-900">{b.name}</div>
                    <div className="text-sm font-semibold text-neutral-800 mt-0.5">{currency(Math.round(a.actual))}/mo</div>
                    {!a.fullyFunded && (
                      <div className="text-[10px] text-neutral-400">
                        {a.actual === 0 ? `Unfunded — ${currency(Math.round(a.requested))}/mo requested` : `of ${currency(Math.round(a.requested))} requested`}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      <div className="flex items-center justify-center text-sm">
        <span className={remaining > 0 ? "text-emerald-600 font-medium" : "text-neutral-500 font-medium"}>
          {remaining > 0 ? `${currency(Math.round(remaining))} unallocated surplus` : "Fully allocated"}
        </span>
      </div>
    </div>
  );
}
